/* eslint-disable @typescript-eslint/no-explicit-any */
import { Flex, AutoComplete } from 'antd';
import { Row } from 'antd/lib';
import { ReactNode } from 'react';
import { Loader } from 'semantic-ui-react';
import { Search } from 'lucide-react';

import SearchTemplateResult from './SearchTemplateResult';
import useSearchTemplateInfinite from './useSearchTemplateInfinite';
import { ITemplate } from './types';
import styles from './SearchTemplate.less';

const THRESHOLD_SCROLL = 60;

interface SearchTemplateProps {
  handleSelect: (template?: ITemplate) => Promise<void>;
  tenantId: string;
  serviceId?: string;
}

const renderTitle = (total: number): ReactNode => (
  <Row className="ac-header">
    <span>{total ? `${total} template(s)` : 'No templates found'}</span>
  </Row>
);

const renderLoadingTitle = (): ReactNode => (
  <Row className="ac-header">
    <Flex align="center" gap={8}>
      <Loader active inline="centered" size="mini" />
      <span>Loading...</span>
    </Flex>
  </Row>
);

function SearchTemplate({ handleSelect, tenantId, serviceId }: SearchTemplateProps) {
  const {
    templates,
    loading,
    fetchNextPage,
    onChangeSearchTerm,
    searchTerm,
    setSearchTerm,
    total,
  } = useSearchTemplateInfinite(tenantId, serviceId);

  const handleSelectOrderTemplate = async (templateId: string): Promise<void> => {
    const selectedTemplate = templates.find((e: ITemplate) => e.id === templateId);
    setSearchTerm(selectedTemplate?.templateName || '');
    await handleSelect(selectedTemplate);
  };

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    const { currentTarget } = e;
    if (
      currentTarget.scrollHeight - THRESHOLD_SCROLL <=
      currentTarget.clientHeight + currentTarget.scrollTop
    ) {
      fetchNextPage();
    }
  };

  return (
    <Flex justify="space-between" align="center" gap="large">
      <AutoComplete
        value={searchTerm}
        onSelect={handleSelectOrderTemplate}
        onSearch={onChangeSearchTerm}
        size="middle"
        allowClear
        placeholder="Search prescription template"
        className={styles.searchTemplatesWrapper}
        prefix={<Search size={16} color="#8596a3" />}
        options={[
          {
            label: loading ? renderLoadingTitle() : renderTitle(total),
            options: templates.map((item: ITemplate) => ({
              value: item.id,
              label: <SearchTemplateResult item={item} />,
            })),
          },
        ]}
        onPopupScroll={handlePopupScroll}
      />
    </Flex>
  );
}

export default SearchTemplate;
